import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { DateTime } from 'luxon';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import Avatar from '@material-ui/core/Avatar';
import TextField from '@material-ui/core/TextField';
import Collapse from '@material-ui/core/Collapse';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Tooltip from '@material-ui/core/Tooltip';

import InvitationIcon from '@material-ui/icons/Drafts';
import UserIcon from '@material-ui/icons/Person';
import AddIcon from '@material-ui/icons/Add';

import Loading from 'app/components/loaders/Loading';
import EditGuestDialog from 'app/components/event/PublicEditGuestDialog';
import RedButton from 'app/components/buttons/RedButton';
import ChargePaymentMethodDialog from 'app/components/accounting/payments/ChargePaymentMethodDialog';

import { paper } from 'app/styles/core';
import { container } from 'app/styles/new-layout';
import states from 'app/utils/States';

import useQuery from 'app/hooks/useQuery';
import useApp from 'app/context/useApp';
import useApi from 'app/context/useApi';

import green from '@material-ui/core/colors/green';
import yellow from '@material-ui/core/colors/yellow';

const useStyles = makeStyles(theme => ({
    container: {
        ...container(theme),
        padding: theme.spacing(3),
    },
    containerMobile: {
        width: '100%',
        padding: theme.spacing(3),
    },
    paper: {
        ...paper(theme),
        position: 'relative',
        marginBottom: theme.spacing(3)
    },
    description: {
        maxWidth: 680,
        margin: `${theme.spacing(6)}px auto`,
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
    actionsBanner: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        position: 'relative'
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    marginTop: {
        marginTop: theme.spacing(3),
    },
    marginTopSmall: {
        marginTop: theme.spacing(1),
    },
    marginLeft: {
        marginLeft: theme.spacing(3)
    },
    bottomContainer: {
        marginBottom: theme.spacing(3), 
    },
    textButton: {
        cursor: 'pointer'
    },
    textCenter: {
        textAlign: 'center'
    },
    menu: {
        width: '100%',
    },
    paidIcon: {
        backgroundColor: green[600]
    },
    COMPLIMENTARY: {
        padding: '3px 5px',
        backgroundColor: theme.palette.secondary.main,
        color: 'white',
        borderRadius: '4px'
    },
    PAID: {
        padding: '3px 5px',
        backgroundColor: green[600],
        color: 'white',
        borderRadius: '4px'
    },
    UNPAID: {
        padding: '3px 5px',
        backgroundColor: yellow[800],
        color: 'white',
        borderRadius: '2px'
    },
    PROCESSING: {
        padding: '3px 5px',
        backgroundColor: theme.palette.primary.dark,
        color: 'white',
        borderRadius: '2px'
    },
}));

function getTicket(ticket_id, tickets) {
    return tickets.find((t) => t.id === ticket_id);
}

function getProcessingInvoice(invitation) {
    return invitation?.invoice?.payments?.find((p) => p.status === 'processing');
}

const InvitationListItem = props => {

    const { invitation, handleChange, handleDelete, index, tickets, guests, getErrorMessages, closeExpanded } = props;

    const [ expanded, setExpanded ] = useState(false);
    const [ variants, setVariants ] = useState([]);
    const [ anchorEl, setAnchorEl] = useState(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const classes = useStyles();

    const handleExpand = () => {
        setExpanded(!expanded);
    }

    const handleTicketChange = (event) => {
        const ticket_id = event.target.value;
        const newTicket = getTicket(ticket_id, tickets);

        if (newTicket.variants.length) {
            openVariantDialog(newTicket.variants, event.currentTarget)
        }
        
        handleChange(index, 'ticket_id', ticket_id);
    }

    const handleGuest = name => event => {
        const newGuest = {...invitation.guest}
        let value;

        if (DateTime.isDateTime(event) || event === null) {
            value = event;
        } else {
            const target = event.target;
            value = (target.type === 'checkbox' ? target.checked : target.value);
        }

        newGuest[name] = value;
        handleChange(index, 'guest', newGuest)
    }

    useEffect(() => {
        if (invitation.guest.id === 0) setExpanded(true);
    }, [invitation]);

    useEffect(() => {
        if (closeExpanded) setExpanded(false);
    }, [closeExpanded])

    const openVariantDialog = (vars, t) => {
        setVariants(vars);
        setAnchorEl(t);
    }

    const handleVariantChange = (variant) => {
        handleChange(index, 'variant', variant);
        handleCloseVariantDialog();
    } 

    const handleCloseVariantDialog = () => {
        setAnchorEl(null);
    }

    const errors = getErrorMessages(index);

    const ticket = useMemo(() => getTicket(invitation.ticket_id, tickets), [invitation.ticket_id, tickets])

    const ticketPrice = useMemo(() => {
        // Use invoice item fro ticket price if paid, otherwise use current price
    })

    const status = useMemo(() => {

        const processingInvoice = getProcessingInvoice(invitation);

        if (ticket && ticket.current_price < 0.01) return 'COMPLIMENTARY';
        if (invitation.invoice?.paid) return 'PAID';
        else if (processingInvoice) return 'PROCESSING';
        else return 'UNPAID';
    }, [invitation, ticket]);

    return (
        <Paper className={classes.paper}>
            <ListItem disableGutters>
                { !isMobile &&
                    <ListItemAvatar>
                        <Avatar className={invitation.invoice?.paid ? classes.paidIcon : null}>
                            <InvitationIcon />
                        </Avatar>
                    </ListItemAvatar>
                }
                <ListItemText>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={6}>
                            {invitation.guest.id > 0 ? (
                                <>
                                    <Typography variant="subtitle1" component={isMobile ? "span" : "div"}>
                                        {invitation.guest.full_name}
                                        {(invitation.dateName && invitation.dateName != "") &&
                                            <Typography variant="subtitle1" component="span" color='textSecondary'>
                                                and {invitation.dateName}
                                            </Typography>
                                        }
                                        &nbsp;&nbsp;&nbsp;
                                        <Typography
                                            color="primary"
                                            className={classes[status]}
                                            component="span"
                                            variant="subtitle2"
                                            >
                                            {status}
                                        </Typography>
                                        &nbsp;&nbsp;&nbsp;
                                    </Typography>
                                    <Typography
                                        color="primary"
                                        className={classes.textButton}
                                        onClick={handleExpand}
                                        component={isMobile ? "span" : "div"}
                                        >
                                        {expanded ? "Hide Guest Details" : "Edit Guest"}
                                    </Typography>
                                </>
                            ) : (invitation.guest.first != "" && invitation.guest.last != "") ? (
                                <>
                                    <Typography variant="subtitle1">
                                        {invitation.guest.first}&nbsp;{invitation.guest.last}
                                    </Typography>
                                    <Typography
                                        color="primary"
                                        className={classes.textButton}
                                        onClick={handleExpand}
                                        >
                                        {expanded ? "Hide Guest Details" : "Edit Guest"}
                                    </Typography>
                                </>
                            ) : (
                                <TextField
                                    select
                                    label="Use a Saved Guest?"
                                    value={invitation.status}
                                    onChange={(event) => handleChange(index, 'guest', event.target.value)}
                                    variant="outlined"
                                    classes={{ root: classes.menu }}
                                    >
                                    {guests.map(g => (
                                        <MenuItem key={g.id} value={g}>{g.full_name}</MenuItem>
                                    ))}
                                </TextField>
                            )}
                        </Grid>
                        {(tickets.length > 0) &&
                            <Grid item xs={12} md={6}>
                                <TextField
                                    select
                                    label="Ticket"
                                    value={invitation.ticket_id}
                                    onChange={handleTicketChange}
                                    variant="outlined"
                                    classes={{ root: classes.menu }}
                                    disabled={status === 'PAID' || status === 'PROCESSING' || invitation.entered_at !== null}
                                    fullWidth
                                    required
                                    >
                                    {tickets.map(t => (
                                        <MenuItem key={t.id} value={t.id}>
                                            <Grid 
                                                container
                                                justify="space-between"
                                                alignItems="center"
                                                >
                                                <Grid item>{t.title} {(invitation.variant && invitation.variant != "") ? "(" + invitation.variant + ")" : null}</Grid>
                                                <Grid item>
                                                    <Typography
                                                        component="span"
                                                        color="primary"
                                                        variant="inherit"
                                                        >
                                                        ${t.current_price}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        }
                    </Grid>
                    <Collapse in={expanded}>
                        <Grid container spacing={3} alignItems="flex-start" className={classes.marginTopSmall}>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    label="Prefix"
                                    value={invitation.guest.prefix}
                                    onChange={handleGuest('prefix')}
                                    variant="outlined"
                                    fullWidth
                                    disabled={invitation.entered_at !== null}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="First"
                                    value={invitation.guest.first}
                                    onChange={handleGuest('first')}
                                    variant="outlined"
                                    fullWidth
                                    required
                                    disabled={invitation.entered_at !== null}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    label="Middle"
                                    value={invitation.guest.middle}
                                    onChange={handleGuest('middle')}
                                    variant="outlined"
                                    fullWidth
                                    disabled={invitation.entered_at !== null}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Last"
                                    value={invitation.guest.last}
                                    onChange={handleGuest('last')}
                                    variant="outlined"
                                    fullWidth
                                    required
                                    disabled={invitation.entered_at !== null}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    label="Suffix"
                                    value={invitation.guest.suffix}
                                    onChange={handleGuest('suffix')}
                                    variant="outlined"
                                    fullWidth
                                    disabled={invitation.entered_at !== null}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={3} alignItems="flex-start" className={classes.marginTopSmall}>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="Address One"
                                    value={invitation.guest.address_one}
                                    onChange={handleGuest('address_one')}
                                    variant="outlined"
                                    fullWidth
                                    disabled={invitation.entered_at !== null}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    label="Address Two"
                                    value={invitation.guest.address_two}
                                    onChange={handleGuest('address_two')}
                                    variant="outlined"
                                    fullWidth
                                    disabled={invitation.entered_at !== null}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    label="City"
                                    value={invitation.guest.city}
                                    onChange={handleGuest('city')}
                                    variant="outlined"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    select
                                    label="State"
                                    value={invitation.guest.state}
                                    onChange={handleGuest('state')}
                                    fullWidth
                                    variant="outlined"
                                    disabled={invitation.entered_at !== null}
                                    >
                                    {states.map( state => {
                                        return (
                                            <MenuItem key={state.abbreviation} value={state.abbreviation} >{state.name}</MenuItem>
                                        )
                                    })}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    label="Zip"
                                    value={invitation.guest.zip}
                                    onChange={handleGuest('zip')}
                                    variant="outlined"
                                    fullWidth
                                    disabled={invitation.entered_at !== null}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={3} alignItems="flex-start" className={classes.marginTopSmall}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Email"
                                    value={invitation.guest.email}
                                    onChange={handleGuest('email')}
                                    variant="outlined"
                                    fullWidth
                                    disabled={invitation.entered_at !== null}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Phone"
                                    value={invitation.guest.phone_mobile}
                                    onChange={handleGuest('phone_mobile')}
                                    variant="outlined"
                                    fullWidth
                                    disabled={invitation.entered_at !== null}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {(invitation.ticket_id > 0 && ticket?.dates_allowed > 0 && invitation.entered_at === null) &&
                                    <TextField
                                        label="Date Name"
                                        value={invitation.dateName}
                                        onChange={(event) => handleChange(index, 'dateName', event.target.value)}
                                        variant="outlined"
                                        fullWidth
                                        disabled={invitation.ticket_id < 1 || ticket?.dates_allowed < 1 || invitation.entered_at !== null}
                                    />
                                }
                            </Grid>
                        </Grid>
                        <Grid container spacing={3} justify="space-between" alignItems="center" className={classes.marginTopSmall}>
                            <Grid item xs={12} md={3}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={invitation.snailmail_invite}
                                            onChange={(event) => handleChange(index, 'snailmail_invite', event.target.checked)}
                                            value="snailmail_invite"
                                            disabled={
                                                invitation.guest.address_one === "" || 
                                                invitation.guest.city === "" ||
                                                invitation.guest.state === "" ||
                                                invitation.entered_at !== null
                                            }
                                        />
                                    }
                                    label="Send a Physical Invitation?"
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <RedButton
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => handleDelete(index)}
                                    disabled={invitation?.invoice?.paid || invitation.entered_at !== null}
                                    >
                                    Delete Invitation
                                </RedButton>
                            </Grid>
                        </Grid>
                    </Collapse>
                    {(errors.length > 0) &&
                        <Typography color="error" variant="subtitle2" className={classes.marginTopSmall}>
                            <ul>
                                {errors.map((error, key) => (
                                    <li key={key}>{error}</li>
                                ))}
                            </ul>
                        </Typography>
                    }
                </ListItemText>
            </ListItem>
            <Menu
                id={`variant-menu-${index}`}
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleCloseVariantDialog}
                >
                <MenuItem
                    key={0}
                    disabled
                    >
                    Please Select a Ticket Variant
                </MenuItem>
                {variants.map((option, i) => (
                    <MenuItem
                        key={i + 1}
                        onClick={() => handleVariantChange(option)}
                        >
                        {option}
                    </MenuItem>
                ))}
            </Menu>
        </Paper>
    )
}

const beforeUnloadHandler = (event) => {
    // Recommended
    event.preventDefault();
  
    // Included for legacy support, e.g. Chrome/Edge < 119
    event.returnValue = true;
};

function attachDateNames(invitation) {
    invitation.guest.invitations_sent.forEach((inv, i) => {
        invitation.guest.invitations_sent[i]['dateName'] = inv?.dates[0]?.name;
    })
    return invitation;
}

export default function PublicGuestForm (props) {

    const { app } = useApp();
    let query = useQuery();
    const api = useApi();
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [ invitationId, setInvitationId ] = useState(0);
    const [ hash, setHash ] = useState('');
    const [ invitation, setInvitation ] = useState(null);
    const [ invitations, setInvitations ] = useState([]);
    const [ guests, setGuests ] = useState([]);
    const [ tab, setTab ] = useState('invitations');

    const [ guestDialogOpen, setGuestDialogOpen ] = useState(false);
    const [ editingUser, setEditingUser ] = useState(null);

    const [ errors, setErrors ] = useState({});
    const [ loading, setLoading ] = useState(false);

    const [ invitationsUpdated, setInvitationsUpdated ] = useState(false);
    const [ chargePaymentMethodDialogOpen, setChargePaymentMethodDialogOpen ] = useState(false);
    const [ closeExpanded, setCloseExpanded ] = useState(false);

    const getForm = (i,h) => {

        const data = {
            invitationId: i ?? invitationId,
            hash: h ?? hash,
        }

        api.post('/event/guest-form', data)
            .then(res => {
                res.invitation = attachDateNames(res.invitation);
                setInvitation(res.invitation);
                setLoading(false)
                setInvitations(res.invitation.guest.invitations_sent);
                setGuests(res.guests);
                setCloseExpanded(false);
            }).catch(error => {
                if (error?.response?.data?.errors) setErrors(error.response.data.errors);
                setLoading(false)
            });
    }

    useEffect(() => {

        setLoading(true);

        const queryInvitationId = query.get('inv');
        const queryHash = query.get('hash');

        setInvitationId(queryInvitationId);
        setHash(queryHash);

        if (! queryInvitationId || ! queryHash) {
            setErrors({general: 'We could not validate your request. Please try the guest form link again or contact support.'})
            setLoading(false);
        } else {
            getForm(queryInvitationId, queryHash);
        }
    }, []);

    useEffect(() => {
        if (invitationsUpdated === true) {
            window.addEventListener("beforeunload", beforeUnloadHandler);
        } else {
            window.removeEventListener("beforeunload", beforeUnloadHandler);
        }
    }, [invitationsUpdated]);

    // General Functions ------------------------------------------

    const createNewListItem = () => {
    
        let newItem;

        newItem = {
            guest: {
                id: 0,
                prefix: "",
                first: "",
                middle: "",
                last: "",
                suffix: "",
                address_one: "",
                address_two: "",
                city: "",
                state: "",
                zip: "",
                email: "",
                phone_mobile: ""
            },
            id: 0,
            ticket_id: 0,
            title: "You have been invited to " + invitation.event.name,
            message: "I would like to invite you as my guest to " + invitation.event.name + ". Thank you for your consideration!",
            dateName: "",
            snailmail_invite: false,
            entered_at: null
        }

        setInvitations([...invitations, newItem ]);
        setInvitationsUpdated(true);
    }

    const handleListItem = (i, field, value) => {
        const newInvitations = [...invitations];

        newInvitations[i][field] = value;

        setInvitations(newInvitations);
        setInvitationsUpdated(true);
    }

    const deleteListItem = i => {
        const newInvitations = [...invitations];

        newInvitations.splice(i, 1);

        setInvitations(newInvitations);
        setInvitationsUpdated(true);
    }

    // Post Functions ------------------------------------------

    const postInvitations = () => {
        if (invitations.length > 0 && invitationsUpdated) {
            setLoading(true);
            setErrors({});

            const data = {};

            data.invitations = invitations;
            data.hash = hash;
            data.invitationId = invitationId;

            api.post('/event/guest-form/invitations', data)
                .then(() => {
                    setInvitationsUpdated(false);
                    getForm();
                    setCloseExpanded(true);
                }).catch( error => {
                    if (error?.response?.data?.errors) {
                        setErrors(error.response.data.errors)
                    }
                    setLoading(false)
                });
        }
    }

    // Pay Balance Screen
    const payBalance = () => {

    }


    const hasErrors = key => {
        if (errors[key]) return true;
        return false;
    }

    const getErrorMessages = key => {
        if (hasErrors(key)) return errors[key];
        return "";
    }

    // Charge Payment Dialog Functions ///////////////////////////////////////////////////////////////

    const openChargePaymentMethodDialog = () => {
        setChargePaymentMethodDialogOpen(true);
    }

    const closeChargePaymentMethodDialog = () => {
        setChargePaymentMethodDialogOpen(false);
    }

    const closeChargePaymentMethodDialogAndRefresh = () => {
        closeChargePaymentMethodDialog();
        getForm();
    }

    // Editing User Dialog Functions ///////////////////////////////////////////////////////////////

    const openGuestDialog = (u = null) => {
        if (u) setEditingUser(u);
        console.log(u)
        setGuestDialogOpen(true)
    }

    const closeGuestDialog = () => {
        setGuestDialogOpen(false);
        getForm();
    }

    const guestDialogClosed = () => {
        setEditingUser(null);
    }

    const ticketTotal = useMemo(() => {
        let total = 0;
        
        invitations.forEach((i) => {
            if (i.ticket) {

                const processingInvoice = getProcessingInvoice(i);

                if (!i.invoice || (i.invoice && !processingInvoice && !i.invoice.paid)) total += parseInt(i.ticket.current_price);
                // else ticket has been paid and not added to outstanding balance
            } else {
                const ticket = getTicket(i.ticket_id, invitation.available_tickets_for_guests);

                if (ticket) total += parseInt(ticket.current_price);
            }
        });

        return total;
    }, [invitations, invitation]);

    const outstandingInvoice = useMemo(() => {

        const outstandingInvitation = invitations.find((i) => {

            const processingInvoice = getProcessingInvoice(i);

            if (i.invoice && !processingInvoice && !i.invoice.paid) return true;
            return false;
        });

        return outstandingInvitation ? outstandingInvitation.invoice : null;
    }, [invitations, invitation]);

    return (
        <div className={isMobile ? classes.containerMobile : classes.container}>
            <Loading isLoading={loading}/>
            {hasErrors('general') &&
                <Alert severity="error">{getErrorMessages('general')}</Alert>
            }
            <Grid container justifyContent='space-between' alignItems='flex-start' spacing={3}>
                {invitation &&
                    <Grid item>
                        <Typography variant="h5" component="h2">
                            {invitation.event.name}
                        </Typography>
                    </Grid>
                }
                <Grid item>
                    <ButtonGroup variant="outlined" className={classes.marginBottom} color="secondary">
                        <Button variant={(tab === 'invitations') ? 'contained' : 'outlined'} onClick={() => setTab('invitations')} >Your Invitations ({invitations.length})</Button>
                        <Button variant={(tab === 'event') ? 'contained' : 'outlined'} onClick={() => setTab('event')}>Event Details</Button>
                    </ButtonGroup>
                </Grid>
            </Grid>
            {tab === 'invitations' ? (
                <>
                    {invitation &&
                        <form noValidate autoComplete="off" className={classes.section}>
                            <Grid
                                container
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={3}
                                className={classes}
                                >
                                <Grid item>
                                    <Typography variant="h6" component="h3">
                                        Your Invitations
                                    </Typography>
                                </Grid>
                            </Grid>
                            <List>
                                {invitations.length ? invitations.map((i, key) => (
                                    <InvitationListItem
                                        key={key}
                                        invitation={i}
                                        index={key}
                                        handleChange={handleListItem}
                                        handleDelete={deleteListItem}
                                        tickets={invitation.available_tickets_for_guests}
                                        guests={guests}
                                        hasErrors={hasErrors}
                                        getErrorMessages={getErrorMessages}
                                        closeExpanded={closeExpanded}
                                    />
                                )) : (
                                    <Alert severity="info">
                                        To create your first invitation, click <strong>NEW INVITATION</strong> below.
                                    </Alert>
                                )}
                            </List>
                            <Grid
                                container
                                justify="space-between"
                                alignItems="flex-end"
                                spacing={3}
                                className={classes.bottomContainer}
                                >
                                <Grid item xs={12} sm={12} md={6}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={createNewListItem}
                                        color="secondary"
                                        fullWidth={isMobile ? true : false}
                                        >
                                        New Invitation
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={postInvitations}
                                        color="secondary"
                                        disabled={!invitations.length || loading || !invitationsUpdated}
                                        className={isMobile ? classes.marginTopSmall : classes.marginLeft}
                                        fullWidth={isMobile ? true : false}
                                        >
                                        Save Invitations
                                    </Button>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="subtitle2" align="right">
                                        <Typography component="span" variant="inherit">
                                            Outstanding Balance:&nbsp;
                                        </Typography>
                                        <Typography
                                            component="span"
                                            color="primary"
                                            variant="inherit"
                                            >
                                            ${ticketTotal.toFixed(2)}
                                        </Typography>
                                    </Typography>
                                    <Tooltip title={
                                            invitationsUpdated ? "Please save your invitations before paying your balance." 
                                            : !outstandingInvoice ? "You do not have an outstanding balance."
                                            : "Pay Now"}>
                                        <span>
                                            <Button
                                                variant="contained"
                                                onClick={openChargePaymentMethodDialog}
                                                color="secondary"
                                                disabled={!invitations.length || loading || invitationsUpdated || !outstandingInvoice}
                                                className={classes.marginTopSmall}
                                                fullWidth
                                                >
                                                Pay Balance
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </form>
                    }
                </>
            ) : (
                <>
                    <div className={classes.description}>
                        <Typography
                            variant="body1"
                            component="p"
                            color="textSecondary"
                            dangerouslySetInnerHTML={{__html: invitation.event.description}}
                        />
                    </div>  
                    <Divider/>
                    <div className={classes.actionsBanner}>
                        <Grid
                            container
                            alignItems="center"
                            justifyContent="center"
                            spacing={3}
                            >
                            <Grid item xs={12} md={6} className={classes.textCenter}>
                                <Typography variant="h5" align="center" gutterBottom>WHEN</Typography>
                                <Typography variant="subtitle2" align="center">
                                    {DateTime.fromISO(invitation.event.starts_at).toLocaleString(DateTime.DATE_HUGE)}<br/>
                                    {DateTime.fromISO(invitation.event.starts_at).toLocaleString(DateTime.TIME_SIMPLE)}&nbsp;-&nbsp;
                                    {DateTime.fromISO(invitation.event.ends_at).toLocaleString(DateTime.TIME_SIMPLE)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6} className={classes.textCenter}>
                                <Typography variant="h5" align="center" gutterBottom>WHERE</Typography>
                                <Typography variant="subtitle2" align="center">
                                    {invitation.event.location}<br/> 
                                    {invitation.event.address}<br/>
                                    {invitation.event.city} {invitation.event.state}, {invitation.event.zip}
                                </Typography>
                            </Grid>
                        </Grid>
                    </div>
                </>
                
            )}
            <Alert severity="warning">
                To receive a physical invitation, the invitation must be saved on or before April 1st. Any invitations created after April 1st will not be eligible for a physical invitation. 
            </Alert>
            {outstandingInvoice &&
                <ChargePaymentMethodDialog
                    user={invitation?.guest}
                    club={app.club}
                    urlPrefix={`/global/${invitationId}/${hash}`}
                    invoice={outstandingInvoice}
                    open={chargePaymentMethodDialogOpen}
                    onClose={closeChargePaymentMethodDialog}
                    onCharge={closeChargePaymentMethodDialogAndRefresh}
                />
            }
        </div>
    );
}